'use client';

/**
 * WebMediaShowcaseCard — a full-bleed graphic (video / react_component /
 * image) framed by a short prose intro. Deliberately smaller than
 * WebHeroCard: no headline array, no CTA, no stats — this renders
 * MediaShowcaseCardContent, for "here's the thing running" sections
 * (a real walkthrough video, a process diagram) rather than a page's
 * opening pitch.
 *
 * No hardcoded strings — eyebrow / heading / body / caption / fineprint
 * all read from the spec. The graphic itself is resolved by
 * MediaRenderer, which dispatches on the media resource's real kind
 * (video/react_component/image/svg/lottie) — this card never knows
 * which format it got.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useEffect, useState } from 'react';
import { Loader2, XCircle, ImageOff } from 'lucide-react';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { api } from '@/lib/api/client';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useLanguageStore } from '@/lib/stores/language-store';
import { MediaRenderer } from './media-renderer';
import type { WebMediaShowcaseCardContent } from '@/lib/api/types';

interface WebMediaShowcaseCardProps {
  /** Capability action — which content spec this instance fetches.
   *  Same renderer, different prose + graphic per strategyAction. */
  strategyAction: string;
}

export function WebMediaShowcaseCard({ strategyAction }: WebMediaShowcaseCardProps) {
  const strategy = useCapabilityStore((s) => s.strategyName('content', strategyAction));
  const lang = useLanguageStore((s) => s.lang) ?? undefined;

  const [data, setData] = useState<WebMediaShowcaseCardContent | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!strategy) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    api
      .getStrategyData<WebMediaShowcaseCardContent>(strategy, lang ? { lang } : {})
      .then((res) => {
        if (cancelled) return;
        if (!res.success) {
          setError(res.error || 'Failed to load content');
          return;
        }
        setData(res.data ?? null);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message ?? 'Network error');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [strategy, lang]);

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        Loading…
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-destructive">
        <XCircle className="h-6 w-6 mb-2" />
        <p className={text.body}>{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <ImageOff className="h-8 w-8 mb-2 opacity-40" />
        <p className={text.body}>Content not available.</p>
      </div>
    );
  }

  return (
    <section className="web-surface bg-background text-foreground py-20">
      <div className="container mx-auto px-6 max-w-4xl">
        {data.eyebrow && (
          <span className={cn(text.eyebrow, 'block mb-4 text-primary uppercase tracking-wide')}>
            {data.eyebrow}
          </span>
        )}
        <h2 className={cn(text.title, 'text-3xl md:text-4xl font-semibold mb-4')}>
          {data.heading}
        </h2>
        {data.body && (
          <p className={cn(text.body, 'max-w-2xl mb-10')}>{data.body}</p>
        )}

        <div className="rounded-lg border border-border overflow-hidden bg-card">
          <MediaRenderer mediaRef={data.graphic.media_ref} className="w-full" />
        </div>

        {(data.caption || data.fineprint) && (
          <div className="mt-4">
            {data.caption && <p className={cn(text.body, 'text-muted-foreground')}>{data.caption}</p>}
            {data.fineprint && (
              <p className={cn(text.muted, 'mt-1 font-mono text-xs')}>{data.fineprint}</p>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
